/**
 * Запросы к `/api/public/analytics/*` (US-04.1…04.8) — типизированные
 * контракты под виджеты раздела «Аналитика» (as-is `09_analytics_api.md`).
 *
 * Параметры одинаковы для всех эндпоинтов: `kato` (обязательный) и `year`
 * (опциональный; без него бэк отдаёт последний доступный год — фактический
 * год приходит в поле `year` ответа).
 *
 * Все ответы прогоняются через нормализаторы: пустое тело, отсутствующие
 * поля или мусор вместо ожидаемых типов превращаются в безопасные значения
 * (`null` / `[]` / отфильтрованные элементы). `null`/`[]` = «данных нет»
 * (виджет скрывается), `0` — реальный ноль из БД. Сетевые/HTTP-ошибки
 * реджектятся — их обрабатывает вызывающий код.
 */
import { api } from './api';
import type {
  AgricultureAnalyticsResponse,
  AnalyticsAllResponse,
  BudgetExecution,
  BudgetExpenditureRow,
  BudgetIncomeRow,
  LegalTaxesCompany,
  LegalTaxesPayments,
  LegalTaxesResponse,
  LegalTaxesSectionCompanies,
  ConstructionAnalyticsResponse,
  ConstructionMetric,
  EconomyAnalyticsResponse,
  InfrastructureAnalyticsResponse,
  LandFundAnalyticsResponse,
  PopulationAnalyticsResponse,
  RoadNetwork,
  SocialAnalyticsResponse,
} from '../types/analytics';
import type {
  SnpCount,
  SnpEducation,
  SnpHealthcare,
  SnpNeedsData,
} from '../types/snpNeeds';

// --- примитивы нормализации (как в public.request.ts) ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.length > 0 ? v : null;

/** Число из числа или числовой строки (КАТО «Мониторинга СНП» — строкой). */
const numLoose = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) {
    return Number(v);
  }
  return null;
};

const bool = (v: unknown): boolean => v === true;

// --- нормализаторы ответов ---

export const normalizePopulationAnalytics = (
  raw: unknown,
): PopulationAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const movement = root.natural_movement == null ? null : asObj(root.natural_movement);
  const groups = root.social_groups == null ? null : asObj(root.social_groups);

  return {
    year: num(root.year),
    plashka: {
      population: num(plashka.population),
      economically_active_population: num(
        plashka.economically_active_population,
      ),
      youth: num(plashka.youth),
      unemployment_rate: num(plashka.unemployment_rate),
      unemployed: num(plashka.unemployed),
      saldo_migration: num(plashka.saldo_migration),
    },
    // Staging отдаёт дубли строк по году — берём первую запись.
    population_dynamics: (() => {
      const seen = new Set<number>();
      return asArr(root.population_dynamics)
        .flatMap((entry) => {
          const e = asObj(entry);
          const year = num(e.year);
          if (year === null || seen.has(year)) return [];
          seen.add(year);
          return [{ year, value: num(e.value), forecast: bool(e.forecast) }];
        })
        .sort((a, b) => a.year - b.year);
    })(),
    natural_movement: movement && {
      births: num(movement.births),
      deaths: num(movement.deaths),
      natural_increase: num(movement.natural_increase),
      immigration: num(movement.immigration),
      emigration: num(movement.emigration),
      saldo_migration: num(movement.saldo_migration),
    },
    social_groups: groups && {
      youth: num(groups.youth),
      hired: num(groups.hired),
      self_employed: num(groups.self_employed),
      unemployed: num(groups.unemployed),
      working_elderly: num(groups.working_elderly),
      tsa_recipients: num(groups.tsa_recipients),
      pensioners: num(groups.pensioners),
      large_families: num(groups.large_families),
      disabled: num(groups.disabled),
    },
    demography: asArr(root.demography).flatMap((entry) => {
      const e = asObj(entry);
      const age = str(e.age);
      if (age === null) return [];
      return [{ age, population: num(e.population) }];
    }),
    labor_market: asArr(root.labor_market)
      .flatMap((entry) => {
        const e = asObj(entry);
        const year = num(e.year);
        if (year === null) return [];
        return [
          {
            year,
            economically_active: num(e.economically_active),
            employed: num(e.employed),
            avg_salary: num(e.avg_salary),
          },
        ];
      })
      .sort((a, b) => a.year - b.year),
  };
};

const budgetExecutionFrom = (raw: unknown): BudgetExecution | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    dev_programs_count: num(e.dev_programs_count),
    cur_year_dev_budget: num(e.cur_year_dev_budget),
    prev_year_dev_budget: num(e.prev_year_dev_budget),
    active_programs_count: num(e.active_programs_count),
    cur_year_active_budget: num(e.cur_year_active_budget),
    prev_year_active_budget: num(e.prev_year_active_budget),
    total_programs_count: num(e.total_programs_count),
    total_cur_year_budget: num(e.total_cur_year_budget),
    total_prev_year_budget: num(e.total_prev_year_budget),
    dev_budget_processing: num(e.dev_budget_processing),
    active_budget_processing: num(e.active_budget_processing),
    total_budget_processing: num(e.total_budget_processing),
  };
};

export const normalizeEconomyAnalytics = (
  raw: unknown,
): EconomyAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const msbRaw = root.msb == null ? null : asObj(root.msb);
  const subjects =
    msbRaw && msbRaw.subjects != null ? asObj(msbRaw.subjects) : null;

  return {
    year: num(root.year),
    plashka: {
      avg_salary: num(plashka.avg_salary),
      investment_ifo: num(plashka.investment_ifo),
      investment_val: num(plashka.investment_val),
      budget_execution_pct: num(plashka.budget_execution_pct),
      tax_revenues_fact: num(plashka.tax_revenues_fact),
      industry_ifo: num(plashka.industry_ifo),
      industry_val: num(plashka.industry_val),
    },
    // Ответ может содержать дубли по subtype — берём первую запись.
    economic_development: (() => {
      const seen = new Set<string>();
      return asArr(root.economic_development).flatMap((entry) => {
        const e = asObj(entry);
        const subtype = str(e.subtype);
        if (subtype === null || seen.has(subtype)) return [];
        seen.add(subtype);
        return [
          {
            subtype,
            current_year_val: num(e.current_year_val),
            previous_year_val: num(e.previous_year_val),
            ifo: num(e.ifo),
            change_pct: num(e.change_pct),
            increase_val: num(e.increase_val),
            ifo_place: num(e.ifo_place),
          },
        ];
      });
    })(),
    budget_execution: budgetExecutionFrom(root.budget_execution),
    // «Освоение бюджета» / «Доходы» из analytics-БД (DP-134); вне СКО — [].
    budget_expenditure: asArr(root.budget_expenditure).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      const row: BudgetExpenditureRow = {
        name,
        plan_yearly: num(e.plan_yearly),
        plan_period: num(e.plan_period),
        fact_period: num(e.fact_period),
        execution_pct: num(e.execution_pct),
      };
      return [row];
    }),
    budget_income: asArr(root.budget_income).flatMap((entry) => {
      const e = asObj(entry);
      const ktkName = str(e.ktk_name);
      if (ktkName === null) return [];
      const row: BudgetIncomeRow = {
        ktk_name: ktkName,
        plan_yearly: num(e.plan_yearly),
        plan_period: num(e.plan_period),
        fact_period: num(e.fact_period),
        execution_pct: num(e.execution_pct),
      };
      return [row];
    }),
    income_tax: asArr(root.income_tax).map((entry) => {
      const e = asObj(entry);
      return {
        type: str(e.type),
        tax_revenues_fact: num(e.tax_revenues_fact),
        tax_revenues_plan: num(e.tax_revenues_plan),
        tax_revenues_annual: num(e.tax_revenues_annual),
        non_tax_revenues_fact: num(e.non_tax_revenues_fact),
        non_tax_revenues_plan: num(e.non_tax_revenues_plan),
        non_tax_revenues_annual: num(e.non_tax_revenues_annual),
        fixed_assets_sale_fact: num(e.fixed_assets_sale_fact),
        fixed_assets_sale_plan: num(e.fixed_assets_sale_plan),
        fixed_assets_sale_annual: num(e.fixed_assets_sale_annual),
      };
    }),
    msb: msbRaw && {
      subjects: subjects && {
        active_count: num(subjects.active_count),
        registered_count: num(subjects.registered_count),
      },
      activities: asArr(msbRaw.activities).flatMap((entry) => {
        const e = asObj(entry);
        const activity = str(e.activity);
        if (activity === null) return [];
        return [{ activity, count: num(e.count) }];
      }),
      financial: asArr(msbRaw.financial).flatMap((entry) => {
        const e = asObj(entry);
        const kind = str(e.kind);
        if (kind === null) return [];
        return [{ kind, projects: num(e.projects), amount: num(e.amount) }];
      }),
    },
  };
};

export const normalizeSocialAnalytics = (
  raw: unknown,
): SocialAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  return {
    year: num(root.year),
    plashka: {
      unemployed: num(plashka.unemployed),
      self_sufficiency_pct: num(plashka.self_sufficiency_pct),
      total_schools: num(plashka.total_schools),
      total_students: num(plashka.total_students),
      total_hospitals: num(plashka.total_hospitals),
    },
    // Staging отдаёт дубли по продукту — берём первую запись.
    self_sufficiency: (() => {
      const seen = new Set<string>();
      return asArr(root.self_sufficiency).flatMap((entry) => {
        const e = asObj(entry);
        const product = str(e.product);
        if (product === null || seen.has(product)) return [];
        seen.add(product);
        return [{ product, pct: num(e.pct) }];
      });
    })(),
    education: asArr(root.education).flatMap((entry) => {
      const e = asObj(entry);
      const type = str(e.type);
      if (type === null) return [];
      return [{ type, schools: num(e.schools), students: num(e.students) }];
    }),
    healthcare: asArr(root.healthcare).flatMap((entry) => {
      const e = asObj(entry);
      const type = str(e.type);
      if (type === null) return [];
      return [{ type, count: num(e.count) }];
    }),
  };
};

export const normalizeAgricultureAnalytics = (
  raw: unknown,
): AgricultureAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  return {
    year: num(root.year),
    plashka: {
      cattle_heads: num(plashka.cattle_heads),
      self_sufficiency_pct: num(plashka.self_sufficiency_pct),
      producers: num(plashka.producers),
      meat_total_t: num(plashka.meat_total_t),
      gross_harvest_ths_t: num(plashka.gross_harvest_ths_t),
    },
    sown_areas: asArr(root.sown_areas).flatMap((entry) => {
      const e = asObj(entry);
      const crop = str(e.crop);
      if (crop === null) return [];
      return [
        {
          crop,
          area_ths_ha: num(e.area_ths_ha),
          gross_harvest_ths_t: num(e.gross_harvest_ths_t),
          producers: num(e.producers),
        },
      ];
    }),
    sown_total: (() => {
      if (root.sown_total == null) return null;
      const e = asObj(root.sown_total);
      return {
        area_ths_ha: num(e.area_ths_ha),
        gross_harvest_ths_t: num(e.gross_harvest_ths_t),
      };
    })(),
    livestock_dynamics: asArr(root.livestock_dynamics)
      .flatMap((entry) => {
        const e = asObj(entry);
        const year = num(e.year);
        if (year === null) return [];
        return [
          {
            year,
            cattle: num(e.cattle),
            small_cattle: num(e.small_cattle),
            horses: num(e.horses),
            birds: num(e.birds),
            camels: num(e.camels),
          },
        ];
      })
      .sort((a, b) => a.year - b.year),
    livestock: asArr(root.livestock).flatMap((entry) => {
      const e = asObj(entry);
      const animal = str(e.animal);
      if (animal === null) return [];
      return [
        {
          animal,
          kh_too: num(e.kh_too),
          lph: num(e.lph),
          total: num(e.total),
          meat_direction: num(e.meat_direction),
          breeding: num(e.breeding),
          cows: num(e.cows),
        },
      ];
    }),
    poultry: asArr(root.poultry).flatMap((entry) => {
      const e = asObj(entry);
      const kind = str(e.kind);
      if (kind === null) return [];
      return [
        {
          kind,
          kh_too: num(e.kh_too),
          lph: num(e.lph),
          total: num(e.total),
        },
      ];
    }),
    poultry_total: num(root.poultry_total),
    meat_production: asArr(root.meat_production).flatMap((entry) => {
      const e = asObj(entry);
      const animal = str(e.animal);
      if (animal === null) return [];
      return [{ animal, tons: num(e.tons) }];
    }),
    dairy_production: asArr(root.dairy_production).flatMap((entry) => {
      const e = asObj(entry);
      const product = str(e.product);
      if (product === null) return [];
      return [
        {
          product,
          annual_output_t: num(e.annual_output_t),
          milk_consumption_ths_l: num(e.milk_consumption_ths_l),
        },
      ];
    }),
  };
};

export const normalizeLandFundAnalytics = (
  raw: unknown,
): LandFundAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const settlement = root.settlement == null ? null : asObj(root.settlement);
  const reserve = root.reserve == null ? null : asObj(root.reserve);
  return {
    year: num(root.year),
    plashka: {
      total_land_ha: num(plashka.total_land_ha),
      arable_ha: num(plashka.arable_ha),
      pasture_ha: num(plashka.pasture_ha),
      reserve_total_ha: num(plashka.reserve_total_ha),
      farm_enterprises: num(plashka.farm_enterprises),
    },
    settlement: settlement && {
      settlement_land_ha: num(settlement.settlement_land_ha),
      agricultural_total_ha: num(settlement.agricultural_total_ha),
      agricultural_arable_ha: num(settlement.agricultural_arable_ha),
      agricultural_pasture_ha: num(settlement.agricultural_pasture_ha),
      farm_enterprises: num(settlement.farm_enterprises),
    },
    reserve: reserve && {
      reserve_total_ha: num(reserve.reserve_total_ha),
      reserve_arable_ha: num(reserve.reserve_arable_ha),
      reserve_pasture_ha: num(reserve.reserve_pasture_ha),
      other_land_ha: num(reserve.other_land_ha),
    },
    landowners: asArr(root.landowners).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      return [
        {
          name,
          assigned_land_ha: num(e.assigned_land_ha),
          assigned_arable_ha: num(e.assigned_arable_ha),
          assigned_pasture_ha: num(e.assigned_pasture_ha),
          fact_land_ha: num(e.fact_land_ha),
          fact_arable_ha: num(e.fact_arable_ha),
          fact_pasture_ha: num(e.fact_pasture_ha),
        },
      ];
    }),
  };
};

const roadNetworkFrom = (raw: unknown): RoadNetwork | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    year: num(e.year),
    total_km: num(e.total_km),
    normative_pct: num(e.normative_pct),
    roads: asArr(e.roads).flatMap((entry) => {
      const road = asObj(entry);
      const key = str(road.key);
      if (key === null) return [];
      return [
        {
          key,
          name_ru: str(road.name_ru) ?? '',
          total_km: num(road.total_km),
          normative_km: num(road.normative_km),
          normative_pct: num(road.normative_pct),
          satisfactory_km: num(road.satisfactory_km),
          satisfactory_pct: num(road.satisfactory_pct),
          bad_km: num(road.bad_km),
          bad_pct: num(road.bad_pct),
        },
      ];
    }),
  };
};

export const normalizeInfrastructureAnalytics = (
  raw: unknown,
): InfrastructureAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  const water = root.water_supply == null ? null : asObj(root.water_supply);
  return {
    year: num(root.year),
    plashka: {
      clean_water_pct: num(plashka.clean_water_pct),
      gas_coverage_pct: num(plashka.gas_coverage_pct),
      internet_coverage_pct: num(plashka.internet_coverage_pct),
      roads_normative_pct: num(plashka.roads_normative_pct),
      roads_total_km: num(plashka.roads_total_km),
    },
    engineering: asArr(root.engineering).flatMap((entry) => {
      const e = asObj(entry);
      const key = str(e.key);
      if (key === null) return [];
      return [
        {
          key,
          snp_coverage_pct: num(e.snp_coverage_pct),
          population_coverage_pct: num(e.population_coverage_pct),
          growth_pct: num(e.growth_pct),
          // Наличие по СНП (мониторинг СНП, лист 5) и чипы операторов.
          covered_snp: num(e.covered_snp),
          total_snp: num(e.total_snp),
          operators:
            e.operators == null
              ? null
              : asArr(e.operators).flatMap((operatorEntry) => {
                  const operator = asObj(operatorEntry);
                  const name = str(operator.name);
                  if (name === null) return [];
                  return [{ name, covered_snp: num(operator.covered_snp) }];
                }),
        },
      ];
    }),
    water_supply: water && {
      network_length_km: num(water.network_length_km),
      clean_water_access_pct: num(water.clean_water_access_pct),
      centralized_water_pct: num(water.centralized_water_pct),
      decentralized_water_pct: num(water.decentralized_water_pct),
      trucked_water_population: num(water.trucked_water_population),
      no_access_population: num(water.no_access_population),
    },
    roads: roadNetworkFrom(root.roads),
  };
};

const constructionMetricFrom = (raw: unknown): ConstructionMetric | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return {
    value: num(e.value),
    ifo: num(e.ifo),
    change_pct: num(e.change_pct),
  };
};

export const normalizeConstructionAnalytics = (
  raw: unknown,
): ConstructionAnalyticsResponse => {
  const root = asObj(raw);
  const plashka = asObj(root.plashka);
  return {
    year: num(root.year),
    plashka: {
      construction_ifo: num(plashka.construction_ifo),
      construction_growth: num(plashka.construction_growth),
      housing_ifo: num(plashka.housing_ifo),
      psd_count: num(plashka.psd_count),
      psd_total_cost: num(plashka.psd_total_cost),
    },
    construction: constructionMetricFrom(root.construction),
    housing: constructionMetricFrom(root.housing),
    psd_by_category: asArr(root.psd_by_category).map((entry) => {
      const e = asObj(entry);
      return {
        category: str(e.category),
        total_count: num(e.total_count) ?? 0,
        government_investors_count: num(e.government_investors_count) ?? 0,
        non_government_investors_count:
          num(e.non_government_investors_count) ?? 0,
        quasi_government_investors_count:
          num(e.quasi_government_investors_count) ?? 0,
      };
    }),
    psd_registry: asArr(root.psd_registry).flatMap((entry) => {
      const e = asObj(entry);
      const project = str(e.project);
      // Строка реестра без наименования проекта — нечего показывать.
      if (project === null) return [];
      return [
        {
          project,
          category: str(e.category),
          financing_source: str(e.financing_source),
          process: str(e.process),
          status: str(e.status),
          cost: num(e.cost),
          realization: str(e.realization),
          // Карточка проекта (FR-5.4, реестр заключений ГГЭ) — имена
          // полей = колонки БД `project_estimate_documentation`;
          // опциональны до передеплоя бэка.
          customer: str(e.customer),
          customer_bin: str(e.customer_bin),
          designer: str(e.designer),
          designer_bin: str(e.designer_bin),
          conclusion_number: str(e.conclusion_number),
          conclusion_date: str(e.conclusion_date),
          examination_company: str(e.examination_company),
          examination_company_bin: str(e.examination_company_bin),
          // КАТО может прийти и числом — показываем строкой.
          kato:
            str(e.kato) ?? (num(e.kato) !== null ? String(e.kato) : null),
          object_location: str(e.object_location),
        },
      ];
    }),
    psd_registry_total: num(root.psd_registry_total),
  };
};

/**
 * «Потребности СНП» — облегчённая версия (US-04.9.1), структура по
 * прототипу `snp-passport-district (1).html`. Эндпоинт
 * `/public/analytics/snp-needs` на backend ещё НЕ реализован: контракт
 * заложен заранее, до появления реста запрос отклоняется 404 — секция
 * показывает заголовки блоков с подписью «Нет данных», без ошибки.
 */
/**
 * `/analytics/snp-needs` (US-04.9.1) — «Мониторинг СНП», агрегация до
 * района/области считается на бэке.
 *
 * Ключевая особенность контракта: почти каждый показатель приходит парой
 * `{present, answered}` — «сколько сёл под условием» и «сколько сёл вообще
 * ответило по этому показателю». Графы «Мониторинга» заполнены неравномерно
 * (автобусное сообщение — 45 сёл из 585, медпункт — 512), поэтому доли и
 * подписи «N из M» считаются от `answered` показателя, а не от общего числа
 * сёл. `null` вместо объекта = данных по теме нет вовсе.
 *
 * Ключи ответа переводятся в ключи словаря i18n (`topics.*`,
 * `healthcare.facilities.*`), незнакомые — отбрасываются.
 */

/** Ключ отклонения в ответе → ключ подписи `analytics.snpNeeds.topics.*`. */
const SNP_DEVIATION_KEYS: Record<string, string> = {
  lep_wear_over_75: 'linesWear',
  tp_wear_over_threshold: 'substationsWear',
  road_condition_poor: 'innerRoads',
  no_bus: 'busService',
  no_mobile_internet: 'internet',
  doctors_deficit: 'doctorsDeficit',
  nurses_deficit: 'nursesDeficit',
  centralized_water_low: 'centralWater',
  delivered_water_population: 'deliveredWater',
  school_places_needed: 'schoolPlaces',
  kindergarten_places_needed: 'kindergartenPlaces',
};

/** Тип медучреждения в ответе → ключ подписи `healthcare.facilities.*`. */
const SNP_FACILITY_KEYS: Record<string, string> = {
  crb: 'crh',
  district_hospital: 'rb',
  rural_hospital: 'sb',
  polyclinic: 'policlinic',
  outpatient_clinic: 'ambulatory',
  fap: 'fap',
  medical_point: 'medpoint',
};

/** Бакет износа в ответе → ключ подписи `engineering.electricity.*`. */
const SNP_WEAR_KEYS: Array<[string, string]> = [
  ['under_50_pct', 'under50'],
  ['between_50_75_pct', 'mid'],
  ['over_75_pct', 'over75'],
];

/** Пара «под условием / ответило»; нет объекта → null. */
const snpCountFrom = (raw: unknown): SnpCount | null => {
  if (raw == null) return null;
  const e = asObj(raw);
  return { present: num(e.present), answered: num(e.answered) };
};

/** «Обратная» пара: сёла, НЕ попавшие под условие (нет связи, плохая вода). */
const snpCountInverted = (raw: unknown): SnpCount | null => {
  const pair = snpCountFrom(raw);
  if (pair === null) return null;
  const missing =
    pair.present !== null && pair.answered !== null
      ? pair.answered - pair.present
      : null;
  return { present: missing, answered: pair.answered };
};

/**
 * Бакеты износа `{under_50_pct, between_50_75_pct, over_75_pct, total}` —
 * это счётчики СЁЛ, а не проценты; доли считаем от `total`.
 */
const snpWearFrom = (
  raw: unknown,
): Array<{ key: string; villages: number | null; pct: number | null }> => {
  if (raw == null) return [];
  const e = asObj(raw);
  const total = num(e.total);
  return SNP_WEAR_KEYS.flatMap(([field, key]) => {
    const villages = num(e[field]);
    if (villages === null) return [];
    return [
      {
        key,
        villages,
        pct: total !== null && total > 0 ? (villages / total) * 100 : null,
      },
    ];
  });
};

export const normalizeSnpNeeds = (raw: unknown): SnpNeedsData => {
  const root = asObj(raw);

  const water = root.water == null ? null : asObj(root.water);
  const engineering =
    root.engineering == null ? null : asObj(root.engineering);
  const coverage =
    engineering && engineering.coverage != null
      ? asObj(engineering.coverage)
      : null;
  const roads = root.roads == null ? null : asObj(root.roads);
  const healthcare = root.healthcare == null ? null : asObj(root.healthcare);
  const facilities =
    healthcare && healthcare.facilities != null
      ? asObj(healthcare.facilities)
      : null;
  const staff =
    healthcare && healthcare.staff != null ? asObj(healthcare.staff) : null;
  const deviations =
    root.deviations == null ? null : asObj(root.deviations);
  const distribution =
    root.deviation_distribution == null
      ? null
      : asObj(root.deviation_distribution);

  // Население — сумма по источникам воды: тот же знаменатель, от которого
  // бэк считает centralized_level_pct / decentralized_level_pct.
  const waterPeople = water
    ? [
        num(water.centralized_population),
        num(water.decentralized_population),
        num(water.delivered_population),
      ]
    : [];
  const population = waterPeople.some((value) => value !== null)
    ? waterPeople.reduce<number>((acc, value) => acc + (value ?? 0), 0)
    : null;

  const waterSource = (
    key: string,
    people: number | null,
    pct: number | null,
  ): Array<{ key: string; people: number | null; pct: number | null }> =>
    people === null && pct === null
      ? []
      : [
          {
            key,
            people,
            pct:
              pct ??
              (people !== null && population !== null && population > 0
                ? (people / population) * 100
                : null),
          },
        ];

  // «Образование»: дефициты приходят готовыми (deficit ≤ 0 — профицит),
  // подвоз — парой school_busing «N из M ответивших».
  const education = root.education == null ? null : asObj(root.education);
  const educationData: SnpEducation | null = education && {
    kindergartenCapacity: num(education.kindergarten_capacity),
    kindergartenDeficit: num(education.kindergarten_deficit),
    schoolCapacity: num(education.school_capacity),
    schoolDeficit: num(education.school_deficit),
    schoolDistanceKm: num(education.school_distance_km),
    busingOrganized: null,
    busing: snpCountFrom(education.school_busing),
  };

  const healthcareData: SnpHealthcare | null = healthcare && {
    // Эндпоинт «сёл без единого учреждения» не отдаёт (US-03 / бэк).
    villagesNoFacility: null,
    facilities: Object.entries(SNP_FACILITY_KEYS).flatMap(
      ([field, key]) => {
        const count = snpCountFrom(facilities?.[field]);
        return count === null ? [] : [{ key, count }];
      },
    ),
    beds: num(staff?.hospital_beds),
    pharmacies: num(staff?.pharmacies),
    totalFacilities: num(staff?.total_facilities),
    staff: staff && {
      doctorsStaffed: num(staff.doctors_staffed),
      doctorsActual: num(staff.doctors_actual),
      doctorsDeficitPct: num(staff.doctors_deficit_pct),
      nursesStaffed: num(staff.nurses_staffed),
      nursesActual: num(staff.nurses_actual),
      nursesDeficitPct: num(staff.nurses_deficit_pct),
    },
  };

  // Состояние дорог приходит только в км — доли считаем от суммы известных
  // состояний (village_roads_total_km в «Мониторинге» с ней не сходится).
  const conditionDefs: Array<[string, number | null]> = roads
    ? [
        ['good', num(roads.condition_good_km)],
        ['satisfactory', num(roads.condition_fair_km)],
        ['bad', num(roads.condition_poor_km)],
      ]
    : [];
  const conditionSum = conditionDefs.reduce(
    (acc, [, km]) => acc + (km ?? 0),
    0,
  );

  const pavedKm = roads ? num(roads.village_roads_paved_km) : null;
  const roadsTotalKm = roads ? num(roads.village_roads_total_km) : null;

  return {
    year: num(root.year),
    totalVillages: num(root.settlement_count),
    population,
    // Темы отклонений — фиксированный список ключей, по убыванию.
    themes: deviations
      ? Object.entries(SNP_DEVIATION_KEYS)
          .flatMap(([field, key]) => {
            const pair = snpCountFrom(deviations[field]);
            if (pair === null || pair.present === null) return [];
            return [
              { key, villages: pair.present, answered: pair.answered },
            ];
          })
          .sort((a, b) => (b.villages ?? 0) - (a.villages ?? 0))
      : [],
    // Список сёл бэк отдаёт уже отсортированным (deviation_count desc,
    // при равенстве — code_kato).
    villages: asArr(root.top_settlements_by_deviations).flatMap((entry) => {
      const e = asObj(entry);
      const name = str(e.name);
      if (name === null) return [];
      const kato = numLoose(e.code_kato);
      return [
        {
          kato,
          name,
          okrug: str(e.okrug),
          population: num(e.population),
          // Перечень тем по селу эндпоинт не отдаёт — только количество.
          deviations: [],
          deviationCount: num(e.deviation_count),
          hasData: kato !== null,
        },
      ];
    }),
    distribution: distribution && {
      none: num(distribution.none),
      low: num(distribution.one_to_two),
      mid: num(distribution.three_to_four),
      high: num(distribution.five_plus),
    },
    engineering: {
      water: water && {
        sources: [
          ...waterSource(
            'central',
            num(water.centralized_population),
            num(water.centralized_level_pct),
          ),
          ...waterSource(
            'decentral',
            num(water.decentralized_population),
            num(water.decentralized_level_pct),
          ),
          ...waterSource('delivered', num(water.delivered_population), null),
        ],
        delivered: snpCountFrom(deviations?.delivered_water_population),
        // quality_compliant — сёла с водой В НОРМЕ; показываем обратное.
        badQuality: snpCountInverted(water.quality_compliant),
      },
      electricity: engineering && {
        wearDistribution: snpWearFrom(engineering.lep_wear_buckets),
        tpWearDistribution: snpWearFrom(engineering.tp_wear_buckets),
        avgWearPct: num(engineering.lep_wear_pct),
        linesKm: num(engineering.lep_length_km),
        substations: num(engineering.tp_count),
        centralized: snpCountFrom(coverage?.centralized_electricity_coverage),
      },
      gas: engineering && {
        housesPct: num(engineering.gasified_houses_pct),
        natural: snpCountFrom(coverage?.gas_supply_coverage),
        lpg: snpCountFrom(coverage?.liquefied_gas_coverage),
        gasifiedPopulation: num(engineering.gasified_population),
      },
      comms: coverage && {
        mobile: snpCountFrom(coverage.cellular_coverage),
        mobileInternet: snpCountFrom(coverage.mobile_internet_coverage),
        broadband: snpCountFrom(coverage.broadband_coverage),
        publicBroadband: snpCountFrom(
          coverage.public_institution_broadband_coverage,
        ),
      },
    },
    roads: roads && {
      totalKm: roadsTotalKm,
      condition: conditionDefs.flatMap(([key, km]) =>
        km === null
          ? []
          : [
              {
                key,
                km,
                pct: conditionSum > 0 ? (km / conditionSum) * 100 : null,
              },
            ],
      ),
      surface: {
        hardKm: pavedKm,
        hardPct:
          pavedKm !== null && roadsTotalKm !== null && roadsTotalKm > 0
            ? (pavedKm / roadsTotalKm) * 100
            : null,
        // «Без покрытия» = всего минус с твёрдым покрытием: surface_dirt_km
        // «Мониторинга» с общей протяжённостью не сходится (по СКО 4 368,9
        // км грунтовых при 4 309,6 км всех дорог), поэтому берём разность,
        // а к грунтовке падаем только когда покрытых дорог нет в ответе.
        noneKm:
          pavedKm !== null && roadsTotalKm !== null
            ? Math.max(0, roadsTotalKm - pavedKm)
            : num(roads.surface_dirt_km),
        capitalRepairKm: num(roads.capital_repair_needed_km),
        mediumRepairKm: num(roads.medium_repair_needed_km),
      },
      transport: {
        bus: snpCountFrom(roads.bus_service),
        offroadKm: null,
        offroadVillages: null,
        routesTotalKm: null,
      },
    },
    education: educationData,
    healthcare: healthcareData,
  };
};

/**
 * `/analytics/all` (US-04.1) — все разделы одним ответом: каждый слайс
 * прогоняется своим нормализатором. Слайса нет (или он `null`) → `null`:
 * секция понимает, что раздел из общего ответа не пришёл, и грузит свой
 * эндпоинт сама (сейчас так с `snp_needs`).
 */
export const normalizeAnalyticsAll = (
  raw: unknown,
): AnalyticsAllResponse => {
  const root = asObj(raw);
  const slice = <T>(
    value: unknown,
    normalize: (raw: unknown) => T,
  ): T | null =>
    typeof value === 'object' && value !== null ? normalize(value) : null;

  return {
    year: num(root.year),
    population: slice(root.population, normalizePopulationAnalytics),
    economy: slice(root.economy, normalizeEconomyAnalytics),
    social: slice(root.social, normalizeSocialAnalytics),
    agriculture: slice(root.agriculture, normalizeAgricultureAnalytics),
    landFund: slice(root.land_fund, normalizeLandFundAnalytics),
    infrastructure: slice(
      root.infrastructure,
      normalizeInfrastructureAnalytics,
    ),
    construction: slice(root.construction, normalizeConstructionAnalytics),
    snpNeeds: slice(root.snp_needs, normalizeSnpNeeds),
  };
};

/**
 * «Налоговые поступления юридических лиц» (форма 2-47, FR-3a) — три
 * эндпоинта `/analytics/legal-entity-taxes*`. Ключ территории — `kato`:
 * бэк сам резолвит его в top_code_region через kato_convert (закрытый
 * открытый вопрос тикета); КАТО без соответствия → 404 с detail.
 *
 * Суммы — тенге (amount_unit: "KZT"); период фактических данных приходит
 * в ответе (period) — фронт его только показывает, дат не передаёт.
 * total === null — данных по территории нет (карточки показывают
 * «Нет данных»; покрывает и 200 с пустым каркасом, и 404 через catch).
 *
 * Таймаут — свой (20 с вместо общих 10 с клиента): основной эндпоинт
 * без дат отвечает ~6 с даже на свежем бэке.
 */
const LEGAL_TAXES_TIMEOUT_MS = 20000;
const legalTaxesCompaniesFrom = (raw: unknown): LegalTaxesCompany[] =>
  asArr(raw).flatMap((entry) => {
    const e = asObj(entry);
    const name = str(e.name);
    if (name === null) return [];
    return [
      {
        bin: str(e.bin),
        name,
        oked: str(e.oked),
        oked_name: str(e.oked_name),
        section: str(e.section),
        amount: num(e.amount),
      },
    ];
  });

export const normalizeLegalTaxes = (raw: unknown): LegalTaxesResponse => {
  const root = asObj(raw);
  const period = root.period == null ? null : asObj(root.period);
  return {
    period: period && {
      date_from: str(period.date_from),
      date_to: str(period.date_to),
    },
    total: num(root.total),
    top: legalTaxesCompaniesFrom(root.top),
    by_activity: asArr(root.by_activity).flatMap((entry) => {
      const e = asObj(entry);
      const section = str(e.section);
      if (section === null) return [];
      return [{ section, amount: num(e.amount), share: num(e.share) }];
    }),
  };
};

export const normalizeLegalTaxesSection = (
  raw: unknown,
): LegalTaxesSectionCompanies => {
  const root = asObj(raw);
  return {
    total: num(root.total),
    items: legalTaxesCompaniesFrom(root.items),
  };
};

export const normalizeLegalTaxesPayments = (
  raw: unknown,
): LegalTaxesPayments => {
  const root = asObj(raw);
  return {
    total: num(root.total),
    items: asArr(root.items).map((entry) => {
      const e = asObj(entry);
      return {
        kbk: str(e.kbk),
        kbk_name: str(e.kbk_name),
        amount: num(e.amount),
        paid_at: str(e.paid_at),
      };
    }),
  };
};

/** Обе диаграммы виджета одним ответом. */
export const getLegalEntityTaxes = async (
  kato: number,
): Promise<LegalTaxesResponse> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes',
    { params: { kato }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxes(response.data);
};

/** Drill-through: ВСЕ компании раздела ОКЭД (section — как в by_activity). */
export const getLegalTaxesByActivity = async (
  kato: number,
  section: string,
): Promise<LegalTaxesSectionCompanies> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes/by-activity',
    { params: { kato, section }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxesSection(response.data);
};

/** Раскрытие строки компании: платежи по БИН (КБК · сумма · дата). */
export const getLegalTaxesPayments = async (
  kato: number,
  bin: string,
): Promise<LegalTaxesPayments> => {
  const response = await api.get<unknown>(
    '/public/analytics/legal-entity-taxes/payments',
    { params: { kato, bin }, timeout: LEGAL_TAXES_TIMEOUT_MS },
  );
  return normalizeLegalTaxesPayments(response.data);
};

// --- запросы ---

const request = async <T>(
  path: string,
  normalize: (raw: unknown) => T,
  kato: number,
  year?: number | null,
): Promise<T> => {
  const params: Record<string, number> = { kato };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>(path, { params });
  return normalize(response.data);
};

export const getAnalyticsAll = (
  kato: number,
  year?: number | null,
): Promise<AnalyticsAllResponse> =>
  request('/public/analytics/all', normalizeAnalyticsAll, kato, year);

export const getAnalyticsPopulation = (
  kato: number,
  year?: number | null,
): Promise<PopulationAnalyticsResponse> =>
  request('/public/analytics/population', normalizePopulationAnalytics, kato, year);

export const getAnalyticsEconomy = (
  kato: number,
  year?: number | null,
): Promise<EconomyAnalyticsResponse> =>
  request('/public/analytics/economy', normalizeEconomyAnalytics, kato, year);

export const getAnalyticsSocial = (
  kato: number,
  year?: number | null,
): Promise<SocialAnalyticsResponse> =>
  request('/public/analytics/social', normalizeSocialAnalytics, kato, year);

export const getAnalyticsAgriculture = (
  kato: number,
  year?: number | null,
): Promise<AgricultureAnalyticsResponse> =>
  request('/public/analytics/agriculture', normalizeAgricultureAnalytics, kato, year);

export const getAnalyticsLandFund = (
  kato: number,
  year?: number | null,
): Promise<LandFundAnalyticsResponse> =>
  request('/public/analytics/land-fund', normalizeLandFundAnalytics, kato, year);

export const getAnalyticsInfrastructure = (
  kato: number,
  year?: number | null,
): Promise<InfrastructureAnalyticsResponse> =>
  request('/public/analytics/infrastructure', normalizeInfrastructureAnalytics, kato, year);

/** Размер страницы «Реестра ПСД» (пагинация — DP-158). */
export const PSD_PAGE_SIZE = 10;

/**
 * Раздел «Строительство»; реестр ПСД пагинирован (psd_limit/psd_offset,
 * DP-158): ответ несёт страницу psd_registry и общий psd_registry_total.
 * Смена страницы — повторный запрос с новым psdOffset (остальные блоки
 * ответа секция не перечитывает).
 */
export const getAnalyticsConstruction = async (
  kato: number,
  year?: number | null,
  psdOffset = 0,
): Promise<ConstructionAnalyticsResponse> => {
  const params: Record<string, number> = {
    kato,
    psd_limit: PSD_PAGE_SIZE,
    psd_offset: psdOffset,
  };
  if (year != null) {
    params.year = year;
  }
  const response = await api.get<unknown>(
    '/public/analytics/construction',
    { params },
  );
  return normalizeConstructionAnalytics(response.data);
};

export const getAnalyticsSnpNeeds = (
  kato: number,
  year?: number | null,
): Promise<SnpNeedsData> =>
  request('/public/analytics/snp-needs', normalizeSnpNeeds, kato, year);
